import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Avatar, Badge, Card, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, relative, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type AdminUser = {
  id: string
  email: string
  display_name: string
  status: string
  roles: string[]
  tier: string
  last_seen_at: string | null
  created_at: string | null
}

type Paginated = { items: AdminUser[]; total: number; page: number; page_size: number }

const PAGE_SIZE = 25

export function statusTone(status: string): 'green' | 'orange' | 'alert' | undefined {
  if (status === 'active') return 'green'
  if (status === 'suspended') return 'alert'
  if (status === 'pending') return 'orange'
  return undefined
}

type CreatedUser = {
  id: string
  email: string
  display_name: string
  roles: string[]
  temporary_password: string
}

/** Mirrors `Role` in app/core/rbac.py, minus `user` (granted to everyone). */
const ASSIGNABLE_ROLES = ['support', 'clinician', 'researcher', 'admin', 'superadmin'] as const

export function Users() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const [draft, setDraft] = useState(q)
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreatedUser | null>(null)
  const { can } = useAuth()

  const users = useResource(
    (signal) =>
      get<Paginated>('/v1/admin/users', { q, page, page_size: PAGE_SIZE }, signal),
    [q, page],
  )

  const applySearch = (event: React.FormEvent) => {
    event.preventDefault()
    setPage(1)
    setParams(draft.trim() ? { q: draft.trim() } : {})
  }

  const pages = users.data ? Math.max(Math.ceil(users.data.total / PAGE_SIZE), 1) : 1

  return (
    <Page
      title="Users"
      subtitle="Accounts, roles and entitlement. Health data lives behind a separate permission and is never shown in this list."
      actions={
        <form className="row" onSubmit={applySearch}>
          {can(P.userWriteAny) && (
            <button className="btn btn--sm btn--primary" type="button" onClick={() => setCreating(true)}>
              Add user
            </button>
          )}
          <input
            className="input"
            style={{ width: 260 }}
            placeholder="Filter by email…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="Filter by email"
          />
          <button className="btn btn--sm" type="submit">
            Search
          </button>
          {q && (
            <button
              className="btn btn--sm btn--ghost"
              type="button"
              onClick={() => {
                setDraft('')
                setParams({})
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </form>
      }
    >
      <Card flush>
        <Loaded resource={users}>
          {(data) => (
            <>
              <Table
                rows={data.items}
                rowKey={(row) => row.id}
                onRowClick={(row) => navigate(`/users/${row.id}`)}
                empty={q ? `No account matches “${q}”.` : 'No accounts yet.'}
                columns={[
                  {
                    key: 'name',
                    header: 'Account',
                    strong: true,
                    render: (row) => (
                      <span className="row">
                        <Avatar name={row.display_name || row.email} />
                        <span>
                          <span style={{ display: 'block' }}>{row.display_name || '—'}</span>
                          <span className="dim" style={{ fontSize: 11.5 }}>
                            {row.email}
                          </span>
                        </span>
                      </span>
                    ),
                  },
                  {
                    key: 'tier',
                    header: 'Tier',
                    render: (row) =>
                      row.tier === 'pro' ? <Badge tone="lime">Pro</Badge> : <Badge>Free</Badge>,
                  },
                  {
                    key: 'roles',
                    header: 'Roles',
                    render: (row) => (
                      <span className="row row--wrap" style={{ gap: 4 }}>
                        {row.roles.map((role) => (
                          <Badge key={role} tone={role === 'superadmin' ? 'alert' : undefined}>
                            {titleCase(role)}
                          </Badge>
                        ))}
                      </span>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (row) => (
                      <Badge tone={statusTone(row.status)}>{titleCase(row.status)}</Badge>
                    ),
                  },
                  {
                    key: 'seen',
                    header: 'Last seen',
                    render: (row) => <span className="dim">{relative(row.last_seen_at)}</span>,
                  },
                  {
                    key: 'created',
                    header: 'Joined',
                    render: (row) => <span className="dim">{relative(row.created_at)}</span>,
                  },
                ]}
              />
              <div className="row" style={{ padding: '12px 16px', borderTop: '1px solid var(--divider)' }}>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  {count(data.total)} account{data.total === 1 ? '' : 's'} · page {data.page} of {pages}
                </span>
                <div className="spacer" />
                <button
                  className="btn btn--sm btn--ghost"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn--sm btn--ghost"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </Loaded>
      </Card>

      {creating && (
        <CreateUser
          onClose={() => setCreating(false)}
          onCreated={(account) => {
            setCreating(false)
            setCreated(account)
            users.reload()
          }}
        />
      )}

      {created && <TemporaryPassword account={created} onClose={() => setCreated(null)} />}
    </Page>
  )
}

function CreateUser({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (account: CreatedUser) => void
}) {
  const run = useAction()
  const { can, user } = useAuth()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [roles, setRoles] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  // The API enforces both of these; mirroring them means the operator sees a
  // disabled checkbox rather than a 403 after filling the whole form.
  const canAssignRoles = can(P.roleWrite)
  const isSuperadmin = (user?.roles ?? []).includes('superadmin')

  const canSave =
    email.trim().length > 3 && displayName.trim().length > 0 && reason.trim().length >= 10

  const toggle = (role: string) =>
    setRoles((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    )

  const save = async () => {
    setBusy(true)
    await run(
      async () => {
        const account = await post<CreatedUser>('/v1/admin/users', {
          email: email.trim(),
          display_name: displayName.trim(),
          roles,
          reason: reason.trim(),
        })
        onCreated(account)
      },
      `${email.trim()} created.`,
    )
    setBusy(false)
  }

  return (
    <Modal title="Add a user" onClose={onClose}>
      <div className="stack">
        <Notice>
          The account starts on a generated password shown once on the next screen. Nothing here
          sends mail — you deliver it, and the person is prompted to replace it when they sign in.
        </Notice>

        <div className="field">
          <label className="field__label" htmlFor="new-email">
            Email
          </label>
          <input
            id="new-email"
            className="input"
            type="email"
            autoComplete="off"
            spellCheck={false}
            value={email}
            autoFocus
            onChange={(event) => setEmail(event.target.value)}
            placeholder="person@example.com"
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="new-name">
            Display name
          </label>
          <input
            id="new-name"
            className="input"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="How they appear in the app"
          />
        </div>

        <div className="field">
          <span className="field__label">Roles</span>
          {canAssignRoles ? (
            <div className="row row--wrap" style={{ gap: 10 }}>
              {ASSIGNABLE_ROLES.map((role) => {
                const blocked = role === 'superadmin' && !isSuperadmin
                return (
                  <label
                    key={role}
                    className="row"
                    style={{ gap: 5, opacity: blocked ? 0.45 : 1 }}
                    title={blocked ? 'Only a superadmin can grant superadmin' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={roles.includes(role)}
                      disabled={blocked}
                      onChange={() => toggle(role)}
                    />
                    <span>{titleCase(role)}</span>
                  </label>
                )
              })}
            </div>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Your account cannot assign roles, so this one is created as a plain user. Someone
              holding <span className="mono">role:write</span> can grant roles afterwards.
            </p>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="new-reason">
            Reason (recorded in the audit log, minimum 10 characters)
          </label>
          <textarea
            id="new-reason"
            className="textarea"
            style={{ minHeight: 72 }}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Ticket reference and who asked for this account"
          />
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * The one screen in this console that displays a live credential.
 *
 * It is shown once and is not recoverable: nothing stores the password in
 * plaintext, so closing this dialog without copying it means the account needs a
 * new one. That is the correct trade — a password an operator can look up later
 * is a password the person does not own.
 */
function TemporaryPassword({ account, onClose }: { account: CreatedUser; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  return (
    <Modal title={`${account.display_name} is ready`} onClose={onClose}>
      <div className="stack">
        <Notice tone="warn">
          This password is shown once. Copy it now — it is not stored anywhere it can be read back,
          so if you lose it the account needs a new one.
        </Notice>

        <div className="field">
          <span className="field__label">Temporary password for {account.email}</span>
          <div className="row" style={{ gap: 8 }}>
            <code className="mono input" style={{ flex: 1, userSelect: 'all' }}>
              {account.temporary_password}
            </code>
            <button
              className="btn btn--sm"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(account.temporary_password)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false))
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <p className="muted" style={{ margin: 0 }}>
          Deliver it over a channel you trust, not the same inbox the account signs in with. The
          person is prompted to replace it on first sign-in, which is what retires your copy.
        </p>

        <div className="row row--wrap" style={{ gap: 4 }}>
          {account.roles.map((role) => (
            <Badge key={role} tone={role === 'superadmin' ? 'alert' : undefined}>
              {titleCase(role)}
            </Badge>
          ))}
        </div>

        <div className="row row--end">
          <button className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  )
}
